import { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useMyVerificationApplication, createVerificationApplication } from '../../hooks/useVerification';
import { LoadingSpinner } from '../common/LoadingSpinner';

export default function ApplyForVerification() {
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { application, loading } = useMyVerificationApplication(currentUser?.uid);
  const navigate = useNavigate();
  const toast = useToast();

  const [realFullName, setRealFullName] = useState('');
  const [bio, setBio] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const isValid =
    realFullName.trim().length >= 2 &&
    bio.trim().length >= 50 &&
    contactEmail.trim().includes('@') &&
    phoneNumber.trim().length >= 7;

  const handleSubmit = async () => {
    if (!currentUser || !userData || !isValid) return;
    setSubmitting(true);
    try {
      await createVerificationApplication({
        realFullName: realFullName.trim(),
        bio: bio.trim(),
        contactEmail: contactEmail.trim(),
        phoneNumber: phoneNumber.trim(),
        userId: currentUser.uid,
        userEmail: userData.email || currentUser.email,
        userAnonymizedUsername: userData.anonymizedUsername,
      });
      toast({
        title: 'Application submitted',
        description: 'We\'ll review your application and get back to you soon.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading..." />;

  // Already has an application — show status
  if (application) {
    const statusConfig = {
      pending: {
        icon: FiClock,
        color: 'orange',
        title: 'Application Under Review',
        message: 'Your verification application has been submitted and is being reviewed by our team. You\'ll receive an email once a decision has been made.',
      },
      approved: {
        icon: FiCheckCircle,
        color: 'green',
        title: 'You\'re Verified!',
        message: 'Your verification has been approved. You can now submit research on tasks.',
      },
      rejected: {
        icon: FiXCircle,
        color: 'red',
        title: 'Application Not Approved',
        message: 'Unfortunately, your verification application was not approved. Please contact support if you have questions.',
      },
    };

    const cfg = statusConfig[application.status] || statusConfig.pending;

    return (
      <Container maxW="2xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Button
            as={Link}
            to="/tasks"
            variant="ghost"
            leftIcon={<Icon as={FiArrowLeft} />}
            alignSelf="flex-start"
            size="sm"
          >
            Back to Tasks
          </Button>

          <Box
            bg={cardBg}
            rounded="xl"
            p={8}
            borderWidth={1}
            borderColor={borderColor}
            textAlign="center"
          >
            <Icon as={cfg.icon} boxSize={12} color={`${cfg.color}.400`} mb={4} />
            <Heading size="md" mb={2}>{cfg.title}</Heading>
            <Text color="gray.600" mb={4}>{cfg.message}</Text>
            {application.status === 'rejected' && application.rejectionReason && (
              <Alert status="error" rounded="md" textAlign="left" mb={4}>
                <AlertIcon />
                <Text fontSize="sm">{application.rejectionReason}</Text>
              </Alert>
            )}
            {application.status === 'approved' && (
              <Button as={Link} to="/tasks" colorScheme="green">
                Browse Tasks
              </Button>
            )}
            {application.status === 'rejected' && (
              <Button as={Link} to="/support/create" colorScheme="orange">
                Contact Support
              </Button>
            )}
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Button
          as={Link}
          to="/tasks"
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Tasks
        </Button>

        <Box>
          <Heading size="lg">Apply for Verification</Heading>
          <Text color="gray.600" mt={1}>
            Get verified to submit research on tasks. We review all applications to maintain the quality of our research community.
          </Text>
        </Box>

        <Box
          bg={cardBg}
          rounded="xl"
          p={6}
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>Full Name</FormLabel>
              <Input
                value={realFullName}
                onChange={(e) => setRealFullName(e.target.value)}
                placeholder="Your real full name"
              />
              <FormHelperText>This will only be visible to admins</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>About You</FormLabel>
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about your background, expertise, and experience relevant to research and attribution..."
                rows={5}
              />
              <FormHelperText>
                {bio.length}/50 characters minimum
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Contact Email</FormLabel>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="your@email.com"
              />
              <FormHelperText>Best email to reach you at</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </FormControl>

            <Button
              colorScheme="orange"
              size="lg"
              onClick={handleSubmit}
              isLoading={submitting}
              isDisabled={!isValid}
              mt={2}
            >
              Submit Application
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}
